import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { apiPaymentStatus, apiBillingSync } from "@/lib/namable";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUsage, track } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error
  const syncedRef = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const s = await apiPaymentStatus(sessionId);
        if (s.payment_status === "paid" && !syncedRef.current) {
          syncedRef.current = true;
          try {
            await apiBillingSync(s.plan || "pro", "web");
          } catch (e) {
            /* backend still source of truth via webhook */
          }
          await refreshUsage();
          track("subscription_completed", { plan: s.plan });
          setState("success");
          return;
        }
        if (["expired", "failed"].includes(s.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        /* keep polling a bit */
      }
      if (tries < 15) timer = setTimeout(poll, 2000);
      else setState(syncedRef.current ? "success" : "error");
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-canvas flex flex-col items-center justify-center px-4 text-center">
      <LogoMark size={44} />
      <div className="mt-8">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-brand mx-auto" size={40} />
            <h1 className="font-display text-[24px] font-extrabold text-ink mt-5">Confirming your subscription…</h1>
            <p className="text-ink-muted text-[14px] mt-2">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="w-16 h-16 rounded-full bg-success-bg flex items-center justify-center mx-auto">
              <CheckCircle2 className="text-success" size={38} />
            </div>
            <h1 className="font-display text-[28px] font-extrabold text-ink mt-5" data-testid="payment-success-title">You're all set!</h1>
            <p className="text-ink-muted text-[15px] mt-2 max-w-[360px]">Your plan is active. More scans, .com search and domain values are unlocked.</p>
            <Button onClick={() => navigate("/generate")} className="mt-7 h-12 px-7 rounded-xl bg-brand hover:bg-brand-dark text-white font-bold btn-glow" data-testid="payment-continue-btn">
              Start naming
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-16 h-16 rounded-full bg-danger-bg flex items-center justify-center mx-auto">
              <XCircle className="text-danger" size={38} />
            </div>
            <h1 className="font-display text-[24px] font-extrabold text-ink mt-5">We couldn't confirm payment</h1>
            <p className="text-ink-muted text-[14px] mt-2 max-w-[360px]">If you were charged, your plan will update shortly. Otherwise, try again.</p>
            <Button onClick={() => navigate("/pricing")} variant="outline" className="mt-7 h-12 px-7 rounded-xl font-bold">
              Back to pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
